import type { DeleteElementConfirmationModalProps } from "@v1/types/modalTypes";
import type { EditorRoute } from "@v2/utils/router";

import * as React from "react";

import { skipToken } from "@tanstack/react-query";
import { successToast } from "@v1/components/designSystem/alert/Toast";
import { elementTypeToEditorData } from "@v1/components/editor/element";
import useSetDraftElement from "@v1/hooks/useSetDraftElement";
import { useV1ErrorToast } from "@v1/hooks/useV1ErrorToast";
import {
  deleteElement as deleteElementAction,
  selectDraftElementId,
  selectDraftElementProjectId,
  selectDraftElementType,
} from "@v1/reducers/core-reducer";
import { useEditorDispatch, useEditorSelector } from "@v1/store/store";
import DeleteConfirmationModal from "@v2/components/DeleteConfirmationModal";
import { useModal } from "@v2/store/modalStore";
import { elementRouter, elementUtils } from "@v2/trpc/routes/element";
import { projectUtils } from "@v2/trpc/routes/project";
import { routes } from "@v2/utils/router";
import { generatePath, useNavigate } from "react-router-dom";

export const DeleteElementConfirmationModal = (
  props: DeleteElementConfirmationModalProps,
) => {
  const pageToDelete = props.element.id;
  const modal = useModal();
  const {
    mutate: deleteElement,
    data,
    isPending: isLoading,
    error,
  } = elementRouter.delete.useMutation({
    onSuccess: () => {
      void projectUtils.listWithStats.invalidate();
      void elementUtils.listAllElementsMetadata.invalidate();
    },
  });
  const draftElementId = useEditorSelector(selectDraftElementId);
  const draftElementProjectId = useEditorSelector(selectDraftElementProjectId);
  const draftElementType = useEditorSelector(selectDraftElementType);
  const setDraftElement = useSetDraftElement();

  const nextElementId = React.useMemo(() => {
    if (!props.elements?.length) {
      return null;
    }
    const currentIndex = props.elements?.findIndex(
      (p) => p.id === pageToDelete,
    );
    const nextIndex = currentIndex === 0 ? currentIndex + 1 : currentIndex - 1;
    return props.elements[nextIndex]?.id;
  }, [pageToDelete, props.elements]);

  const { data: nextElementMetadata } =
    elementRouter.getElementMetadataById.useQuery(nextElementId ?? skipToken);

  const dispatch = useEditorDispatch();
  const navigate = useNavigate();
  const errorToast = useV1ErrorToast();
  const handleDeletion = React.useCallback(() => {
    if (props.type === "unpublish") {
      props.onDelete();
    } else if (props.type === "delete" && pageToDelete) {
      void deleteElement(pageToDelete);
    }
  }, [deleteElement, pageToDelete, props]);

  const pageEditorData = elementTypeToEditorData[props.element?.type];

  React.useEffect(() => {
    if (
      props.type === "delete" &&
      pageToDelete &&
      data?.elementId &&
      pageToDelete === data.elementId
    ) {
      dispatch(deleteElementAction(pageToDelete));
      successToast(
        `${pageEditorData.singularDisplayName} Deleted`,
        `${pageEditorData.singularDisplayName} deleted successfully! 🎉`,
      );
      modal.closeModal({});

      // redirect to another page or article if it's deleting the current draft one
      if (
        draftElementId === data.elementId &&
        draftElementProjectId &&
        props.elements.length > 1 &&
        nextElementMetadata
      ) {
        setDraftElement({ id: nextElementMetadata.id });
        navigate(
          generatePath(routes.editor.element, {
            projectId: draftElementProjectId,
            elementId: nextElementMetadata.id,
          } as EditorRoute),
        );
      }
    } else if (error) {
      errorToast(
        "Failed Deleting Element",
        `Failed deleting ${pageEditorData.singularDisplayName}. Please try again or reach out to support@replo.app for help.`,
        "error.element.delete",
        {
          error: error.message,
        },
      );
    }
  }, [
    data,
    draftElementId,
    draftElementProjectId,
    draftElementType,
    error,
    pageToDelete,
    dispatch,
    modal,
    navigate,
    pageEditorData,
    props.type,
    props.elements,
    setDraftElement,
    errorToast,
    nextElementMetadata,
  ]);

  return (
    <DeleteConfirmationModal
      onRequestClose={() => {
        return modal.closeModal({ type: "deleteElementConfirmationModal" });
      }}
      onDelete={handleDeletion}
      assetTypeDisplayName={pageEditorData.singularDisplayName}
      assetName={props.element.name}
      extraMessage={
        props.element.isHomepage
          ? "Because this page is set as your store's homepage, Replo will convert your store's homepage back to the homepage from your Shopify theme."
          : undefined
      }
      isLoading={isLoading}
      confirmationType={props.type}
    />
  );
};
